import { useEffect, useState } from 'react';
import { classes } from 'utils';

import styles from './Loader.module.scss';

const ANIMATION_DURATION = 210;

type Props = {
  visible?: boolean;
  size?: string;
  type?: 'skyd' | 'bubbles' | 'spin';
  message?: string;
  delay?: number;
  ease?: boolean | { in?: boolean; out?: boolean };
  className?: string;
};

type Builder = (size: string) => JSX.Element;

type State = 'visible' | 'leaving' | 'hidden';

const Loader = (props: Props) => {
  const { visible = true, type, message, delay } = props;
  const [state, setState] = useState<State>(visible ? 'visible' : 'hidden');

  const ease =
    typeof props.ease === 'boolean'
      ? { in: props.ease, out: props.ease }
      : props.ease || { in: true, out: true };

  useEffect(() => {
    if (visible) {
      setState('visible');
    } else {
      if (ease.out) {
        setState('leaving');
        const timer = setTimeout(() => setState('hidden'), ANIMATION_DURATION);

        return () => clearTimeout(timer);
      }

      setState('hidden');
    }
  }, [ease.out, visible]);

  return state !== 'hidden' ? (
    <div
      className={classes(
        props.className,
        styles.loader,
        styles[state],
        message ? styles.message : '',
        ease.in ? styles.easeIn : '',
        ease.out ? styles.easeOut : ''
      )}
      style={delay ? { animationDelay: '0.5s' } : undefined}
    >
      <div>
        {loaders[type || 'skyd'](props.size || '1em')}
        <span>{message}</span>
      </div>
    </div>
  ) : null;
};

export default Loader;

const loaders: { [key: string]: Builder } = {
  bubbles: size => (
    <svg viewBox="0 0 32 32" width={size} height={size} fill="currentColor">
      {[1, 2, 3].map(i => (
        <circle key={i} transform={`translate(${i * 8} 0)`} cx="0" cy="16" r="0">
          <animate
            attributeName="r"
            values="0; 4; 0; 0"
            dur="1.2s"
            repeatCount="indefinite"
            begin={`${(i - 1) * 0.3}`}
            keyTimes="0;0.2;0.7;1"
            keySplines="0.2 0.2 0.4 0.8;0.2 0.6 0.4 0.8;0.2 0.6 0.4 0.8"
            calcMode="spline"
          />
        </circle>
      ))}
    </svg>
  ),

  spin: size => (
    <svg viewBox="0 0 32 32" width={size} height={size} fill="currentColor">
      <path
        opacity=".25"
        d="M16 0 A16 16 0 0 0 16 32 A16 16 0 0 0 16 0 M16 4 A12 12 0 0 1 16 28 A12 12 0 0 1 16 4"
      />
      <path d="M16 0 A16 16 0 0 1 32 16 L28 16 A12 12 0 0 0 16 4z">
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 16 16"
          to="360 16 16"
          dur="0.8s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  ),

  skyd: size => {
    return (
      <svg viewBox="0 0 32 32" width={size} height={size} fill="currentColor">
        <path
          opacity=".25"
          d="M16 0 A16 16 0 0 0 16 32 A16 16 0 0 0 16 0 M16 4 A12 12 0 0 1 16 28 A12 12 0 0 1 16 4"
        />

        <path opacity=".9" d="M16 0 A16 16 0 0 1 32 16 L28 16 A12 12 0 0 0 16 4z">
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 16 16"
            to="360 16 16"
            dur="1s"
            repeatCount="indefinite"
          />
        </path>

        <path
          opacity=".5"
          d="m5.5 15.7a8.5 8.37 0 0 1 0.229-1.63c0.925-4.29 4.4-7.78 8.72-8.44a10.4 10.3 0 0 1 1.11-0.103c0.286-0.00789 0.571-0.00789 0.769-0.00789 0.158 0 0.251 0 0.48 0.017 0.105 0.00789 0.238 0.0209 0.248 0.0222a5.43 5.35 0 0 1 0.853 0.123c3.89 0.809 6.54 3.1 7.97 6.8 0.0505 0.131 0.0612 0.183-0.113 0.182q-2.03-8e-3 -4.07 0a0.266 0.261 0 0 1-0.255-0.141 6.19 6.09 0 0 0-0.442-0.606c-1.63-1.9-3.69-2.69-6.15-2.25-2.38 0.435-4 1.86-4.89 4.12-0.0943 0.238-0.0837 0.243 0.173 0.243h6.05q4.96 0 9.91 0c0.133 0 0.221 0 0.252 0.188a10.7 10.5 0 0 1-0.844 6.26c-2.02 4.31-6.68 6.74-11.3 5.87-3.48-0.654-5.99-2.64-7.59-5.82a6.31 6.21 0 0 1-0.454-1.16h4.17a0.351 0.345 0 0 1 0.343 0.188 6.39 6.29 0 0 0 10.8 0.0484 6.2 6.1 0 0 0 0.767-1.59h-0.266q-7.93 0-15.8 0a0.345 0.34 0 0 1-0.242-0.0601 0.328 0.323 0 0 1-0.093-0.224c-0.0505-0.251-0.0585-0.312-0.122-0.689-0.0266-0.261-0.0438-0.451-0.0545-0.654-0.0146-0.156-0.0146-0.363-0.0146-0.609"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="360 16 16"
            to="0 16 16"
            dur="2s"
            repeatCount="indefinite"
          />
        </path>
      </svg>
    );
  },
};
